/**
 * Display helpers for the configuration change log (Tools > Change Log).
 */

export type RangePreset = "24h" | "7d" | "30d" | "90d" | "all";

export const RANGE_PRESETS: ReadonlyArray<{ id: RangePreset; label: string }> = [
  { id: "24h", label: "Last 24 hours" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
  { id: "all", label: "All time" },
];

const RANGE_HOURS: Record<Exclude<RangePreset, "all">, number> = {
  "24h": 24,
  "7d": 24 * 7,
  "30d": 24 * 30,
  "90d": 24 * 90,
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
  agent: "Agent",
  platform: "Integration",
  customer_application: "Customer integration",
  customer: "Customer",
  psa_customers: "PSA customer sync",
  msp_org: "MSP organization",
  msp_org_m365: "Delegated mail sender",
  automation_config: "Customer Auto Mode",
  msp_automation_config: "Auto Mode defaults",
  auto_mode_scope: "Auto Mode scope",
  authorization_approvers: "Authorization approvers",
  playbook: "Playbook",
  theo_user: "Theo user",
  guide: "Guide",
  doc: "Doc",
  custom_workflow: "Custom workflow",
  schedule: "Schedule",
  teams_bot: "Teams bot",
  ticket_lifecycle: "Ticket Lifecycle",
};

const ACTION_LABELS: Record<string, string> = {
  create: "Created",
  update: "Updated",
  delete: "Deleted",
  enable: "Enabled",
  disable: "Disabled",
  connect: "Connected",
  disconnect: "Disconnected",
  sync: "Synced",
  grant: "Granted",
  cancel: "Canceled",
};

function sentenceCase(text: string): string {
  const spaced = text.replace(/_/g, " ").trim();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : spaced;
}

export function entityTypeLabel(entityType: string): string {
  return ENTITY_TYPE_LABELS[entityType] ?? sentenceCase(entityType);
}

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? sentenceCase(action);
}

/** `trigger.rules[0].text` -> `Trigger › Rules #1 › Text` */
export function humanizeField(path: string): string {
  return path
    .replace(/\[(\d+)\]/g, (_match, index: string) => ` #${Number(index) + 1}`)
    .split(".")
    .map(sentenceCase)
    .join(" › ");
}

export function formatChangeValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "On" : "Off";
  if (Array.isArray(value)) {
    if (value.length === 0) return "(none)";
    if (value.every((item) => item === null || typeof item !== "object")) return value.map(String).join(", ");
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** Day heading for a change: `Today`, `Yesterday`, or the date. */
export function dayLabel(iso: string, now: Date = new Date()): string {
  const when = new Date(iso);
  const days = Math.round((startOfDay(now) - startOfDay(when)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return when.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

export function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" });
}

/** ISO start of a range preset, or undefined for all time. */
export function rangeStart(preset: RangePreset, now: Date = new Date()): string | undefined {
  if (preset === "all") return undefined;
  return new Date(now.getTime() - RANGE_HOURS[preset] * 3_600_000).toISOString();
}
